import React, { type ReactElement, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { InView } from 'react-intersection-observer';

import DownloadApp from '@components/Paywall/Standard/DownloadApp';
import { useCurrentUser, useEventTracking } from '@hooks';
import { subscribe_path } from '@routes';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';

import { PaywallProps } from '../';
import { SelectedOffer } from '../OfferOption';
import PaywallFullBleedForm from './Form';
import PaywallFullBleedTwoOption from './TwoOption';

const FALLBACK_PAYWALL_SUBTITLE = 'Get full access, explore exclusive reporting.';
const SUBSCRIBE_NOW = 'Subscribe now';

const PaywallFullBleed = (paywallProps: PaywallProps): ReactElement => {
  const { currentUser, isLoadingCurrentUser } = useCurrentUser();
  const { Track, trackEvent } = useEventTracking({
    location: !currentUser ? TrackingLocation.Paywall : TrackingLocation.PaywallTwoOptionFullBleed,
  });
  const [selectedOption, setSelectedOption] = useState<SelectedOffer>(
    paywallProps.paywallLeadOffers[0]
  );

  const handleOptionSelection = (offer: SelectedOffer) => {
    setSelectedOption(offer);
    trackEvent(click({ clickTarget: ClickTarget.Offer, offerCode: offer.code }));
  };

  const handleButtonClick = () => {
    const url = subscribe_path({ offer: selectedOption.code });
    trackEvent(
      click({
        clickTarget: ClickTarget.Subscribe,
        clickText: SUBSCRIBE_NOW,
        clickLink: url,
        offerCode: selectedOption.code,
      })
    );
    window.location.href = url;
  };

  if (isLoadingCurrentUser) return <aside className="w-full" data-testid="paywall" />;

  return (
    <aside className="w-full" data-testid="paywall">
      <Track>
        <InView
          triggerOnce={true}
          onChange={(inView) => {
            inView &&
              trackEvent(impression({ impressionElement: ImpressionElement.PaywallFullBleed }));
          }}
        >
          <p className="mb-0 mt-8 font-suisse-screen text-headline-alt-sm text-grayscale-charcoal sm:mt-16 sm:text-center md:text-left lg:mt-24">
            {paywallProps?.subtitle?.length
              ? ReactHtmlParser(paywallProps.subtitle)
              : FALLBACK_PAYWALL_SUBTITLE}
          </p>
          {!currentUser ? (
            <PaywallFullBleedForm {...paywallProps} />
          ) : (
            <PaywallFullBleedTwoOption
              buttonText={SUBSCRIBE_NOW}
              handleButtonClick={handleButtonClick}
              handleOptionSelection={handleOptionSelection}
              paywallLeadOffers={paywallProps.paywallLeadOffers}
              selectedOption={selectedOption}
            />
          )}
          <DownloadApp contentId={paywallProps.articleId ?? paywallProps.briefingGuid} />
        </InView>
      </Track>
    </aside>
  );
};

export default PaywallFullBleed;
